// MIT License - Copyright (c) 2026 Doug McNall

import React, { useState, lazy, Suspense } from 'react';
import DiplomacyModal from './DiplomacyModal';
import QuickReferenceModal from './QuickReferenceModal';
import ShipComputerModal from './ShipComputerModal';
import { useUI } from '../contexts/UIContext';
import './MoreTab.css';

// Lazy-load minigames — only fetch when the player opens them.
const MiningMiniGame = lazy(() => import('./MiningMiniGame'));
const HackingMiniGame = lazy(() => import('./HackingMiniGame'));

/**
 * More Tab
 * Minigames, settings, help, etc.
 */
const MoreTab = () => {
  const [showMining, setShowMining] = useState(false);
  const [showHacking, setShowHacking] = useState(false);
  const [showDiplomacy, setShowDiplomacy] = useState(false);
  const [showQuickRef, setShowQuickRef] = useState(false);
  const [showComputer, setShowComputer] = useState(false);
  const { openHelp, openReputation, openSettings, openAchievements } = useUI();

  const sections = [
    {
      title: 'MINIGAMES',
      items: [
        { icon: '⛏️', label: 'Mining', onClick: () => setShowMining(true) },
        { icon: '💻', label: 'Hacking', onClick: () => setShowHacking(true) },
        { icon: '🤝', label: 'Diplomacy', onClick: () => setShowDiplomacy(true) }
      ]
    },
    {
      title: 'UTILITIES',
      items: [
        { icon: '🖥', label: 'Ship Computer', onClick: () => setShowComputer(true) },
        { icon: '⚙️', label: 'Settings', onClick: () => openSettings() },
        { icon: '🏆', label: 'Achievements', onClick: () => openAchievements() },
        { icon: '🤝', label: 'Reputation', onClick: () => openReputation() },
        { icon: '❓', label: 'Help', onClick: () => openHelp() },
        { icon: '⌨️', label: 'Keyboard Shortcuts', onClick: () => setShowQuickRef(true) }
      ]
    }
  ];

  return (
    <div className="more-tab">
      <div className="more-tab-header">
        <h2>MORE OPTIONS</h2>
        <p>Minigames, utilities, and settings</p>
      </div>

      <div className="more-tab-content">
        {sections.map(section => (
          <div key={section.title} className="more-section">
            <h3 className="section-title">{section.title}</h3>
            <div className="section-grid">
              {section.items.map(item => (
                <button
                  key={item.label}
                  className="more-button"
                  onClick={item.onClick}
                >
                  <span className="more-icon">{item.icon}</span>
                  <span className="more-label">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Modals (MoreTab-only) */}
      {showMining && (
        <Suspense fallback={null}>
          <MiningMiniGame isOpen={true} onClose={() => setShowMining(false)} />
        </Suspense>
      )}
      {showHacking && (
        <Suspense fallback={null}>
          <HackingMiniGame isOpen={true} onClose={() => setShowHacking(false)} />
        </Suspense>
      )}
      {showDiplomacy && (
        <DiplomacyModal
          isOpen={true}
          onClose={() => setShowDiplomacy(false)}
          alien={{ name: 'Galactic Trader', origin: 'Traders Guild', appearance: { ascii: '👽💼' } }}
        />
      )}
      {showQuickRef && <QuickReferenceModal isOpen={true} onClose={() => setShowQuickRef(false)} />}
      {showComputer && <ShipComputerModal onClose={() => setShowComputer(false)} />}
    </div>
  );
};

export default MoreTab;
